// material-ui
import { alpha } from "@mui/material/styles";

// project import
import getColors from "../../helpers/theme/getColors";
import getShadow from "../../helpers/theme/getShadow";

// ==============================|| BUTTON - COLORS ||============================== //

function getColorStyle({ variant, color, theme }) {
  const colors = getColors(theme, color);
  const { lighter, main, dark, darker, contrastText } = colors;
  const buttonShadow = `${color}Button`;
  const shadows = getShadow(theme, buttonShadow);

  const commonShadow = {
    "&::after": {
      boxShadow: `0 0 5px 5px ${alpha(main, 0.9)}`,
    },
    "&:active::after": {
      boxShadow: `0 0 0 0 ${alpha(main, 0.9)}`,
    },
  };

  switch (variant) {
    case "primaryButton":
      return {
        color: theme.palette.buttonColors.containedColor,
        background: theme.palette.buttonColors.containedMagenta,
        "&:hover": {
          backgroundColor: theme.palette.buttonColors.containedDarkMagenta,
          color: theme.palette.buttonColors.containedHoverColor,
        },
        ...commonShadow,
      };
    case "secondaryButton":
      return {
        color: theme.palette.buttonColors.containedMagenta,
        backgroundColor: "transparent",
        borderRadius: 120,
        border: `1px solid ${theme.palette.buttonColors.containedMagenta}`,
        fontFamily: "'Zen Maru Gothic', Arial, sans-serif",
        fontWeight: 700,
        "&:hover": {
          backgroundColor: theme.palette.buttonColors.containedMagenta,
          color: theme.palette.buttonColors.containedColor,
          border: `1px solid ${theme.palette.buttonColors.containedMagenta}`,
        },
      };
    case "bigButton":
      return {
        color: theme.palette.buttonColors.containedColor,
        backgroundColor: theme.palette.buttonColors.containedMagenta,
        borderRadius: 120,
        border: `1px solid ${theme.palette.buttonColors.containedMagenta}`,
        fontFamily: "'Zen Maru Gothic', Arial, sans-serif",
        fontWeight: 700,
        "&:hover": {
          backgroundColor: theme.palette.buttonColors.containedColor,
          color: theme.palette.buttonColors.containedMagenta,
          border: `1px solid ${theme.palette.buttonColors.containedMagenta}`,
        },
      };
    case "shadow":
      return {
        color: contrastText,
        backgroundColor: main,
        boxShadow: shadows,
        "&:hover": {
          boxShadow: "none",
          backgroundColor: dark,
        },
        ...commonShadow,
      };
    case "outlined":
      return {
        color: main,
        backgroundColor: "#fff",
        border: "1px solid",
        "&:hover": {
          color: dark,
          backgroundColor: "#fff",
          border: "2px solid",
        },
        ...commonShadow,
      };
    case "dashed":
      return {
        color: main,
        borderColor: main,
        backgroundColor: lighter,
        "&:hover": {
          color: dark,
          borderColor: dark,
        },
        ...commonShadow,
      };
    case "link":
      return {
        color: theme.palette.linkStyles.color,
        padding: "2px 8px",
        "&:hover": {
          color: theme.palette.linkStyles.hoverColor,
          backgroundColor: "transparent",
          textDecoration: "underline",
        },
        borderRadius: 0,
        justifyContent: "start",
        ...commonShadow,
      };
    case "text":
    default:
      return {
        color: theme.palette.text.secondary,
        padding: "2px 8px",
        "&:hover": {
          color: darker,
          backgroundColor: lighter,
        },
      };
  }
}

// ==============================|| OVERRIDES - BUTTON ||============================== //

export default function Button(theme) {
  const mk = (variant, color = "primary") =>
    getColorStyle({ variant, color, theme });

  const primaryDashed = getColorStyle({
    variant: "dashed",
    color: "primary",
    theme,
  });
  const primaryShadow = getColorStyle({
    variant: "shadow",
    color: "primary",
    theme,
  });
  const primaryLink = getColorStyle({
    variant: "link",
    color: "primary",
    theme,
  });

  const disabledStyle = {
    "&.Mui-disabled": {
      backgroundColor: theme.palette.grey[200],
      color: theme.palette.grey[800],
    },
  };

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontFamily: "'Zen Maru Gothic', Arial, sans-serif",
          borderRadius: 9999,
          textTransform: "none",
          fontWeight: 700,
          minWidth: "auto",
          paddingInline: theme.spacing(5),
          border: "1px solid transparent",
        },

        containedPrimary: {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.primary.main,
          "&:hover": {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            boxShadow: "none",
          },
        },

        outlinedPrimary: {
          backgroundColor: theme.palette.common.white,
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
          paddingInline: theme.spacing(5),
          boxShadow: "none",
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.common.white,
            borderColor: theme.palette.primary.main,
            boxShadow: "none",
          },
        },
        sizeSmall: { height: 36 },
        sizeMedium: { height: 44 },
        sizeLarge: { height: 52 },
      },

      variants: [
        {
          props: { variant: "primaryButton", color: "magenta" },
          style: mk("primaryButton", "magenta"),
        },
        {
          props: { variant: "secondaryButton", color: "magenta" },
          style: mk("secondaryButton", "magenta"),
        },
        {
          props: { variant: "bigButton", color: "magenta" },
          style: mk("bigButton", "magenta"),
        },
      ],
    },
  };
}
